import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./pop-over-with-clickable-options.scss";

export interface PopOverWithClickableOptionsProps {
  options: {
    text: string;
    icon?: React.ReactNode;
    id: string;
    /** Custom CSS class applied to this option row. */
    className?: string;
  }[];
  onClick: (id: string) => void;
  /** Render the popover through a portal so it escapes overflow-hidden containers. */
  shouldUsePortal?: boolean;
  /** Custom portal mount point. Defaults to document.body. */
  getPortalContainer?: () => HTMLElement;
  /** Ref to the trigger element the portal popover should position itself relative to. */
  anchorRef?: React.RefObject<HTMLElement>;
  /** Alignment relative to the anchor. Defaults to "bottom-start". */
  portalPlacement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
  className?: string;
}

const PORTAL_OFFSET = 4;

export const PopOverWithClickableOptions = (
  props: PopOverWithClickableOptionsProps,
) => {
  const {
    options,
    onClick,
    shouldUsePortal = false,
    getPortalContainer,
    anchorRef,
    portalPlacement = "bottom-start",
    className = "",
  } = props;

  const [position, setPosition] = useState<React.CSSProperties | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const isTop = portalPlacement.startsWith("top");
    const isEnd = portalPlacement.endsWith("end");

    setPosition({
      position: "fixed",
      top: isTop ? undefined : rect.bottom + PORTAL_OFFSET,
      bottom: isTop ? window.innerHeight - rect.top + PORTAL_OFFSET : undefined,
      left: isEnd ? undefined : rect.left,
      right: isEnd ? window.innerWidth - rect.right : undefined,
    });
  }, [anchorRef, portalPlacement]);

  useLayoutEffect(() => {
    if (shouldUsePortal && anchorRef?.current) {
      updatePosition();
    }
  }, [shouldUsePortal, updatePosition]);

  useEffect(() => {
    if (!shouldUsePortal || !anchorRef?.current) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [shouldUsePortal, updatePosition]);

  const content = (
    <div
      ref={popoverRef}
      className={`pop-over-with-clickable-options--container ${className}`.trim()}
      style={shouldUsePortal && position ? position : undefined}
    >
      {options.map((option) => (
        <div
          className={`single-option--container ${option.className ?? ""}`.trim()}
          key={option.id}
          onClick={() => onClick(option.id)}
        >
          <div className="single-option--text">
            {option.text}
          </div>
          {option.icon && (
            <div className="single-option--icon">{option.icon}</div>
          )}
        </div>
      ))}
    </div>
  );

  if (shouldUsePortal) {
    const container = getPortalContainer?.() ?? document.body;
    return createPortal(content, container);
  }

  return content;
};
